import type { RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'

import { Outlet, useLocation } from 'react-router'

import { BlackFridayBanner } from '@/modules/black-friday/ui/components/BlackFridayBanner.tsx'
import { useTheme } from '@/utils/useTheme.tsx'

import { Header } from './header/header.tsx'
import { Sidebar } from './sidebar'

const useOutsideClickAndSwipe = (
  outsideRef: RefObject<HTMLElement>,
  callback: () => void,
): void => {
  useEffect(() => {
    let touchStartX = 0
    let touchEndX = 0

    const handleTouchStart = (event: TouchEvent) => {
      touchStartX = event.changedTouches[0].screenX
    }

    const handleTouchMove = (event: TouchEvent) => {
      touchEndX = event.changedTouches[0].screenX
    }

    const handleTouchEnd = () => {
      // Check if the swipe is from right to left
      if (touchEndX > 0 && touchStartX > touchEndX + 50) {
        // Adjust sensitivity as needed
        callback()
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        !outsideRef.current ||
        !outsideRef.current.contains(event.target as Node)
      ) {
        return
      }

      callback()
    }

    // Add event listeners for clicks and touch events
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleTouchStart)
    document.addEventListener('touchmove', handleTouchMove)
    document.addEventListener('touchend', handleTouchEnd)

    // Clean up event listeners on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [outsideRef, callback])
}

export const Layout = () => {
  const mainRef = useRef<HTMLElement>(null)
  const { blackFridayBannerOpen } = useTheme()

  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)

  const location = useLocation()

  useEffect(() => {
    setSidebarCollapsed(true)
  }, [location.pathname])

  useOutsideClickAndSwipe(mainRef, () => {
    setSidebarCollapsed(true)
  })

  return (
    <>
      <BlackFridayBanner />

      <Header setCollapsed={setSidebarCollapsed} />

      <div className={`${blackFridayBannerOpen ? 'mt-[136px]' : 'mt-[72px]'}`}>
        <Sidebar
          collapsed={sidebarCollapsed}
          onCollapse={setSidebarCollapsed}
        />
        <main
          className="lg:pl-[300px]"
          ref={mainRef}
        >
          <Outlet />
        </main>
      </div>
    </>
  )
}
